import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Check, X, FileText, Globe, Users, Plus } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export default function Admin() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [committees, setCommittees] = useState([]);
  const [newCountry, setNewCountry] = useState({ code: "", name_it: "", name_en: "" });
  const [newCommittee, setNewCommittee] = useState({ name: "", country: "", description: "" });
  const [docUrl, setDocUrl] = useState(null);

  const loadUsers = () => api.get("/admin/users").then(({ data }) => setUsers(data));
  const loadCountries = () => api.get("/countries").then(({ data }) => setCountries(data));
  const loadCommittees = () => api.get("/committees").then(({ data }) => setCommittees(data));

  useEffect(() => {
    if (user === false || (user && user.role !== "admin")) { nav("/"); return; }
    if (user) { loadUsers(); loadCountries(); loadCommittees(); }
  }, [user, nav]);

  if (!user) return <div className="max-w-6xl mx-auto px-4 py-12 text-muted-foreground">{t("common.loading")}</div>;
  const cName = (c) => (lang === "it" ? c.name_it : c.name_en);

  const verify = async (uid, status) => {
    try { await api.post(`/admin/users/${uid}/verify`, { status }); toast.success(t("admin.verified")); loadUsers(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const setRole = async (uid, role) => {
    try { await api.post(`/admin/users/${uid}/role?role=${role}`); toast.success(t("admin.verified")); loadUsers(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const viewDoc = async (path) => {
    try {
      const { data } = await api.get(`/files/${path}`, { responseType: "blob" });
      setDocUrl(URL.createObjectURL(data));
    } catch { toast.error("Error"); }
  };
  const addCountry = async () => {
    try { await api.post("/countries", newCountry); toast.success(t("admin.verified")); setNewCountry({ code: "", name_it: "", name_en: "" }); loadCountries(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const addCommittee = async () => {
    try { await api.post("/committees", newCommittee); toast.success(t("admin.verified")); setNewCommittee({ name: "", country: "", description: "" }); loadCommittees(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const badge = { verified: "text-emerald-600 bg-emerald-50", pending: "text-amber-600 bg-amber-50", rejected: "text-destructive bg-red-50" };

  return (
    <div className="max-w-6xl mx-auto px-4 py-10">
      <h1 className="font-heading text-3xl font-black tracking-tighter flex items-center gap-2"><ShieldCheck className="h-7 w-7 text-primary" />{t("admin.title")}</h1>

      <Tabs defaultValue="verify" className="mt-8">
        <TabsList>
          <TabsTrigger value="verify" data-testid="tab-verify">{t("admin.verify")}</TabsTrigger>
          <TabsTrigger value="countries" data-testid="tab-countries">{t("admin.countries")}</TabsTrigger>
          <TabsTrigger value="committees" data-testid="tab-committees">{t("admin.committees")}</TabsTrigger>
        </TabsList>

        <TabsContent value="verify" className="mt-6">
          <div className="space-y-3">
            {users.map((u) => (
              <Card key={u.id} data-testid={`user-row-${u.id}`} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                <div className="min-w-0">
                  <div className="font-semibold">{u.name} <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${badge[u.verification_status]}`}>{t(`status.${u.verification_status}`)}</span></div>
                  <div className="text-sm text-muted-foreground truncate">{u.email} · {u.country} · doc: {u.document_number}</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {u.id_document_path ? <Button data-testid={`view-doc-${u.id}`} onClick={() => viewDoc(u.id_document_path)} variant="outline" size="sm"><FileText className="h-4 w-4 mr-1" />{t("admin.viewDoc")}</Button> : <span className="text-xs text-muted-foreground">{t("admin.noDoc")}</span>}
                  <Select value={u.role} onValueChange={(v) => setRole(u.id, v)}>
                    <SelectTrigger data-testid={`role-${u.id}`} className="w-40 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="citizen">citizen</SelectItem>
                      <SelectItem value="committee_member">committee_member</SelectItem>
                      <SelectItem value="admin">admin</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button data-testid={`approve-${u.id}`} onClick={() => verify(u.id, "verified")} size="sm" className="bg-emerald-600 hover:bg-emerald-700"><Check className="h-4 w-4" /></Button>
                  <Button data-testid={`reject-${u.id}`} onClick={() => verify(u.id, "rejected")} size="sm" variant="destructive"><X className="h-4 w-4" /></Button>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="countries" className="mt-6 space-y-6">
          <Card className="p-5">
            <h3 className="font-heading font-bold mb-3 flex items-center gap-2"><Plus className="h-4 w-4" />{t("admin.addCountry")}</h3>
            <div className="grid sm:grid-cols-4 gap-3 items-end">
              <div><Label>Code</Label><Input data-testid="nc-code" value={newCountry.code} onChange={(e) => setNewCountry({ ...newCountry, code: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Nome IT</Label><Input data-testid="nc-it" value={newCountry.name_it} onChange={(e) => setNewCountry({ ...newCountry, name_it: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Name EN</Label><Input data-testid="nc-en" value={newCountry.name_en} onChange={(e) => setNewCountry({ ...newCountry, name_en: e.target.value })} className="mt-1.5" /></div>
              <Button data-testid="nc-add" onClick={addCountry}>{t("common.create")}</Button>
            </div>
          </Card>
          <div className="grid sm:grid-cols-3 gap-3">
            {countries.map((c) => <Card key={c.code} className="p-4 flex items-center gap-2"><Globe className="h-4 w-4 text-primary" /><span className="font-semibold">{cName(c)}</span><span className="text-xs text-muted-foreground">{c.code}</span></Card>)}
          </div>
        </TabsContent>

        <TabsContent value="committees" className="mt-6 space-y-6">
          <Card className="p-5">
            <h3 className="font-heading font-bold mb-3 flex items-center gap-2"><Plus className="h-4 w-4" />{t("admin.addCommittee")}</h3>
            <div className="grid sm:grid-cols-4 gap-3 items-end">
              <div className="sm:col-span-2"><Label>Nome</Label><Input data-testid="ncm-name" value={newCommittee.name} onChange={(e) => setNewCommittee({ ...newCommittee, name: e.target.value })} className="mt-1.5" /></div>
              <div>
                <Label>{t("common.country")}</Label>
                <Select value={newCommittee.country} onValueChange={(v) => setNewCommittee({ ...newCommittee, country: v })}>
                  <SelectTrigger data-testid="ncm-country" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{countries.map((c) => <SelectItem key={c.code} value={c.code}>{cName(c)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button data-testid="ncm-add" onClick={addCommittee}>{t("common.create")}</Button>
            </div>
          </Card>
          <div className="grid sm:grid-cols-2 gap-3">
            {committees.map((c) => <Card key={c.id} className="p-4"><div className="font-semibold flex items-center gap-2"><Users className="h-4 w-4 text-primary" />{c.name}</div><div className="text-sm text-muted-foreground">{c.country}</div></Card>)}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!docUrl} onOpenChange={(o) => { if (!o) { setDocUrl(null); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{t("admin.doc")}</DialogTitle></DialogHeader>
          {docUrl && (docUrl.includes("pdf") ? <iframe title="doc" src={docUrl} className="w-full h-[70vh]" /> : <img src={docUrl} alt="doc" data-testid="doc-image" className="w-full rounded-md" />)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
